import { defineMessages } from 'react-intl';

import { So5LeaderboardGroupType } from '@sorare/core/src/__generated__/globalTypes';

export const Mode = {
  PRO: 'PRO',
  ARENA_IN_SEASON: 'ARENA_IN_SEASON',
  ARENA_CLASSIC: 'ARENA_CLASSIC',
} as const;

export type Mode = (typeof Mode)[keyof typeof Mode];

export type ArenaMode = typeof Mode.ARENA_IN_SEASON | typeof Mode.ARENA_CLASSIC;

const ARENA_MODES = [Mode.ARENA_IN_SEASON, Mode.ARENA_CLASSIC] as const;

export const isArenaMode = (value: Mode | undefined): value is ArenaMode =>
  !!value && (ARENA_MODES as readonly string[]).includes(value);

export const LineupMode = {
  ARCADE: 'ARCADE',
  PRO: 'PRO',
  ARENA: 'ARENA',
} as const;

export type LineupMode = (typeof LineupMode)[keyof typeof LineupMode];

export const modeMessages = defineMessages({
  [Mode.PRO]: { defaultMessage: 'Pro' },
  [Mode.ARENA_IN_SEASON]: { defaultMessage: 'Arena Cash' },
  [Mode.ARENA_CLASSIC]: { defaultMessage: 'Arena Essence' },
});

export const seriesModeMessages = defineMessages({
  arcade: { defaultMessage: 'Arcade' },
  squad: { defaultMessage: 'Squad' },
});

export const modeToArenaMode = (mode: Mode): 'cash' | 'essence' =>
  mode === Mode.ARENA_CLASSIC ? 'essence' : 'cash';

export const arenaModeToMode = (arenaMode: string): Mode =>
  arenaMode === 'essence' ? Mode.ARENA_CLASSIC : Mode.ARENA_IN_SEASON;

export const arenaModeToGroupType = (
  arenaMode: string
): So5LeaderboardGroupType =>
  arenaMode === 'cash'
    ? So5LeaderboardGroupType.ARENA_IN_SEASON
    : So5LeaderboardGroupType.ARENA_CLASSIC;

/* COLORS */
const modePrimaryColors: Record<Mode, string> = {
  [Mode.PRO]: 'var(--c-modes-pro-primary)',
  [Mode.ARENA_IN_SEASON]: 'var(--c-modes-s27-arena-primary)',
  [Mode.ARENA_CLASSIC]: 'var(--c-modes-classic-arena-primary)',
};

export const modeSecondaryColors: Record<Mode, string> = {
  [Mode.PRO]: 'var(--c-modes-pro-secondary)',
  [Mode.ARENA_IN_SEASON]: 'var(--c-modes-s27-arena-secondary)',
  [Mode.ARENA_CLASSIC]: 'var(--c-modes-classic-arena-secondary)',
};

const modeTertiaryColors: Record<Mode, string> = {
  [Mode.PRO]: 'var(--c-modes-pro-tertiary)',
  [Mode.ARENA_IN_SEASON]: 'var(--c-modes-s27-arena-tertiary)',
  [Mode.ARENA_CLASSIC]: 'var(--c-modes-classic-arena-tertiary)',
};

export const getModeGradient = (mode: Mode): string =>
  `linear-gradient(to bottom, ${modePrimaryColors[mode]} 0%, ${modeSecondaryColors[mode]} 30%, ${modeTertiaryColors[mode]} 100%)`;

export const arcadeModeGradient =
  'linear-gradient(to bottom, var(--c-modes-arcade-primary) 0%, var(--c-modes-arcade-secondary) 30%, var(--c-modes-arcade-tertiary) 100%)';
